import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Volume2, VolumeX, Pause, Play, RotateCcw } from 'lucide-react';

// Game constants
const CANVAS_WIDTH = 1200;
const CANVAS_HEIGHT = 700;
const FOUNDER_SIZE = 60;
const TARGET_SIZE = 70;
const PROJECTILE_SIZE = 40;
const ACCELERATOR_SIZE = 80;

// Characters data
const INVESTOR_CHARACTERS = [
  { id: 'max', name: 'Max Returns', description: 'Looking for the next major value inflection point.', color: '#4F46E5' },
  { id: 'vera', name: 'Vera Venture', description: 'Seeking transformative science with clear milestones.', color: '#7C3AED' },
  { id: 'penny', name: 'Penny Pipeline', description: 'Interested in de-risked development pathways.', color: '#EC4899' },
  { id: 'don', name: 'Don Dealflow', description: 'Always hunting for the next big opportunity.', color: '#F59E0B' },
];

const PHARMA_CHARACTERS = [
  { id: 'pipeline', name: 'Dr. Pipeline', description: 'Building the next generation of therapeutics.', color: '#059669' },
  { id: 'paula', name: 'Paula Portfolio', description: 'Interested—but only if it fits the pipeline.', color: '#0891B2' },
  { id: 'captain', name: 'Captain Catalyst', description: 'Accelerating innovation through partnerships.', color: '#DC2626' },
  { id: 'nova', name: 'Nova Cure', description: 'Pioneering breakthrough treatments for patients.', color: '#8B5CF6' },
];

// Science objects (Phase 1)
const SCIENCE_OBJECTS = [
  { id: 'publications', name: 'Publications', icon: '📄', color: '#3B82F6' },
  { id: 'data', name: 'Experimental Data', icon: '📊', color: '#10B981' },
  { id: 'patents', name: 'Patents', icon: '🛡️', color: '#F59E0B' },
  { id: 'milestones', name: 'Technical Milestones', icon: '✅', color: '#8B5CF6' },
];

// Strategy tools (Phase 2) - 4 comprehensive ones
const STRATEGY_TOOLS = [
  { id: 'market', name: 'Market Opportunity', icon: '🧭', color: '#EC4899' },
  { id: 'clinical', name: 'Development Path', icon: '🏥', color: '#14B8A6' },
  { id: 'business', name: 'Business Model', icon: '🔗', color: '#EF4444' },
  { id: 'storytelling', name: 'Compelling Story', icon: '💬', color: '#F472B6' },
];

// Founder's hopeful comments when shooting science weapons
const FOUNDER_SCIENCE_COMMENTS = [
  "I swear it will work!",
  "Trust me, the biology is proven!",
  "The data speaks for itself!",
  "This is Nobel Prize-worthy science!",
  "Just look at these results!",
  "The mechanism is rock solid!",
  "We've got peer-reviewed publications!",
  "The preclinical data is beautiful!",
  "This will revolutionize medicine!",
  "Our patents are bulletproof!",
  "The science is undeniable!",
  "We've validated the target!",
  "Phase 1 results were amazing!",
  "Top journals published this!",
  "The experts all agree!",
];

// Founder's mature comments when shooting strategy weapons (Phase 2)
const FOUNDER_STRATEGY_COMMENTS = [
  "Here's the market opportunity!",
  "Let me show you the business case.",
  "This is how we create value.",
  "The commercial path is clear.",
  "Here's why this is investable.",
  "Let me address your key concerns.",
  "This is our go-to-market strategy.",
  "Here's the value inflection point.",
  "We understand the competitive landscape.",
  "This is how we'll generate returns.",
  "Let me show you the unit economics.",
  "Here's our path to profitability.",
  "We know our target customers.",
  "This solves a real market need.",
  "Here's the partnership opportunity.",
  "We've de-risked the development path.",
  "This fits your strategic priorities.",
  "Here's why we're differentiated.",
];

// Reactions based on specific weapons
const WEAPON_REACTIONS = {
  // Science weapons - Phase 1 dodge reactions
  science_dodge: {
    investor: {
      publications: ["Another paper? Where's the commercial application?", "Published research is a start, not a finish.", "Academic success ≠ investment success."],
      data: ["Nice data! But data alone doesn't close deals.", "Interesting numbers... what do they mean for returns?", "Good experiment, but where's the business case?"],
      patents: ["A patent is defensive, not a strategy.", "IP is important, but it's not everything.", "Protected tech is great. Who's going to buy it?"],
      milestones: ["Technical milestone achieved. Commercial milestone?", "Science is progressing. What about the business?", "Good progress, but progress toward what exactly?"],
    },
    pharma: {
      publications: ["Published, yes. But does it fit our pipeline?", "Academic validation noted. Clinical relevance?", "Good science. How does it translate to patients?"],
      data: ["Promising data. Regulatory path?", "Interesting results. Reproducible at scale?", "Good preclinical data. What's the clinical plan?"],
      patents: ["Patent noted. Freedom to operate?", "IP landscape matters. What's your position?", "Protected, but is it differentiated enough?"],
      milestones: ["Milestone hit. What's the next value inflection?", "Technical progress. Development timeline?", "Good work. CMC considerations?"],
    }
  },
  // Science weapons - Phase 2 partial hit reactions  
  science_hit: {
    investor: {
      publications: ["Publications help... but I need more than citations!", "Half credit for the science. Show me strategy!", "Academic credibility noted. Now the business case?"],
      data: ["Data is supporting evidence. Strategy is the story!", "Good data point. But data doesn't raise rounds.", "The science is solid... what's the investment thesis?"],
      patents: ["IP is part of the picture. What's the rest?", "Patent adds value. What's the commercialization plan?", "Protected tech is half the battle. Now sell me!"],
      milestones: ["Progress noted! But milestones alone won't cut it.", "You're advancing. Toward what opportunity?", "Technical execution is there. Commercial vision?"],
    },
    pharma: {
      publications: ["Publication noted. Partnership rationale?", "Good science base. Strategic fit still needed.", "Academic foundation there. Clinical strategy?"],
      data: ["Data supports the thesis. Development plan?", "Evidence is building. Regulatory strategy?", "Good results. Manufacturing feasibility?"],
      patents: ["IP position improving. Deal structure thoughts?", "Patent landscape better. Competitive differentiation?", "Freedom to operate clearer. Now the value prop!"],
      milestones: ["Milestone de-risks somewhat. Next steps?", "Progress noted. Timeline to clinical readiness?", "Advancing well. Partnership terms?"],
    }
  },
  // Strategy weapons - Full hit reactions
  strategy_hit: {
    investor: {
      market: ["NOW I see the market opportunity!", "That's a compelling market thesis!", "The TAM makes sense now. This is investable!", "Market dynamics are clear. I'm interested!"],
      clinical: ["The development path is credible!", "I can see how this gets to market!", "Clear pathway to value inflection!", "De-risked development. That's what I like!"],
      business: ["Business model makes sense!", "I see how this makes money!", "The unit economics work. Let's talk!", "Revenue model is clear. Compelling!"],
      storytelling: ["NOW I understand the vision!", "The story resonates! This is fundable!", "Compelling narrative. I'm bought in!", "You've connected the dots. I'm in!"],
    },
    pharma: {
      market: ["Market positioning is clear now!", "I see the unmet need clearly!", "Patient population well-defined!", "Commercial opportunity is compelling!"],
      clinical: ["Development strategy aligns with our needs!", "Regulatory path is credible!", "Clinical plan makes sense for partnership!", "This fits our development capabilities!"],
      business: ["Partnership structure could work!", "Deal economics are interesting!", "Business terms are discussable!", "Value sharing makes sense!"],
      storytelling: ["The strategic rationale is clear!", "I see why we should partner!", "Compelling case for collaboration!", "You've made the case. Let's explore!"],
    }
  }
};

export default function ScienceInvadersGame() {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const gameLoopRef = useRef(null);
  const keysRef = useRef({});
  
  // Game state
  const [gameState, setGameState] = useState('start'); // start, playing, paused, won, learning
  const [targetType, setTargetType] = useState(null); // 'investor' or 'pharma'
  const [selectedCharacter, setSelectedCharacter] = useState(null);
  const [phase, setPhase] = useState(1);
  const [scienceAttempts, setScienceAttempts] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(false);
  const [showAccelerator, setShowAccelerator] = useState(false);
  const [acceleratorHit, setAcceleratorHit] = useState(false);
  const [selectedTool, setSelectedTool] = useState(0);
  const [currentReaction, setCurrentReaction] = useState(null);
  const [founderComment, setFounderComment] = useState(null);
  const [strategyHits, setStrategyHits] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const [dealDone, setDealDone] = useState(false);
  const [targetComingDown, setTargetComingDown] = useState(false);
  
  // Game entities
  const [founder, setFounder] = useState({ x: CANVAS_WIDTH / 2, y: CANVAS_HEIGHT - 100 });
  const [target, setTarget] = useState({ x: CANVAS_WIDTH / 2, y: 80, direction: 1, dodging: false, dodgeTimer: 0 });
  const [projectiles, setProjectiles] = useState([]);
  const [accelerator, setAccelerator] = useState({ x: CANVAS_WIDTH / 2, y: CANVAS_HEIGHT / 2, visible: false });
  const [particles, setParticles] = useState([]);

  // Game state refs for real-time updates in game loop
  const founderRef = useRef({ x: CANVAS_WIDTH / 2, y: CANVAS_HEIGHT - 100 });
  const targetRef = useRef({ x: CANVAS_WIDTH / 2, y: 80, direction: 1, dodging: false, dodgeTimer: 0 });
  const projectilesRef = useRef([]);
  const particlesRef = useRef([]);
  const launchRef = useRef(null);
  const founderCommentRef = useRef(null);

  // Keep founderComment ref in sync
  useEffect(() => {
    founderCommentRef.current = founderComment;
  }, [founderComment]);

  // Helper to get weapon-specific reaction
  const getWeaponReaction = (reactionType, weaponId) => {
    const targetKey = targetType || 'investor';
    const reactions = WEAPON_REACTIONS[reactionType]?.[targetKey]?.[weaponId];
    if (reactions && reactions.length > 0) {
      return reactions[Math.floor(Math.random() * reactions.length)];
    }
    return "Interesting...";
  };

  // Launch projectile - simplified for Phase 2 (no combinations)
  const launchProjectile = useCallback(() => {
    if (gameState !== 'playing' || dealDone) return;
    
    // Use ref for current founder position
    const currentFounder = founderRef.current;
    
    let projectile;
    
    if (phase === 1) {
      // Science object
      const obj = SCIENCE_OBJECTS[selectedTool];
      projectile = {
        x: currentFounder.x,
        y: currentFounder.y - FOUNDER_SIZE / 2,
        type: 'science',
        data: obj,
        speed: 8,
        width: PROJECTILE_SIZE,
        height: PROJECTILE_SIZE,
      };
      // Note: scienceAttempts is now incremented when projectile actually HITS target (in game loop)
    } else if (showAccelerator && !acceleratorHit) {
      // Shooting at accelerator
      const obj = SCIENCE_OBJECTS[0];
      projectile = {
        x: currentFounder.x,
        y: currentFounder.y - FOUNDER_SIZE / 2,
        type: 'accelerator_shot',
        data: obj,
        speed: 10,
        width: PROJECTILE_SIZE,
        height: PROJECTILE_SIZE,
      };
    } else if (phase === 2) {
      // Phase 2: All weapons available, shoot one at a time
      // First 4 are science, next 4 are strategy
      const allWeapons = [...SCIENCE_OBJECTS, ...STRATEGY_TOOLS];
      const weapon = allWeapons[selectedTool];
      const isStrategy = selectedTool >= SCIENCE_OBJECTS.length;
      
      projectile = {
        x: currentFounder.x,
        y: currentFounder.y - FOUNDER_SIZE / 2,
        type: isStrategy ? 'strategy' : 'science',
        data: weapon,
        speed: isStrategy ? 7 : 8,
        width: isStrategy ? PROJECTILE_SIZE * 1.3 : PROJECTILE_SIZE,
        height: isStrategy ? PROJECTILE_SIZE * 1.3 : PROJECTILE_SIZE,
        homing: isStrategy, // Strategy weapons have slight homing
      };
    }
    
    if (projectile) {
      // Add to ref immediately for game loop
      projectilesRef.current.push(projectile);
      setProjectiles(prev => [...prev, projectile]);
      
      // Show founder's comment based on weapon type
      if (projectile.type === 'science') {
        // Hopeful/naive comments for science weapons
        const comment = FOUNDER_SCIENCE_COMMENTS[Math.floor(Math.random() * FOUNDER_SCIENCE_COMMENTS.length)];
        setFounderComment(comment);
        setTimeout(() => setFounderComment(null), 1500);
      } else if (projectile.type === 'strategy') {
        // Mature/strategic comments for strategy weapons
        const comment = FOUNDER_STRATEGY_COMMENTS[Math.floor(Math.random() * FOUNDER_STRATEGY_COMMENTS.length)];
        setFounderComment(comment);
        setTimeout(() => setFounderComment(null), 1500);
      }
    }
  }, [gameState, phase, selectedTool, showAccelerator, acceleratorHit, dealDone]);

  // Keep ref updated
  useEffect(() => {
    launchRef.current = launchProjectile;
  }, [launchProjectile]);

  // Handle keyboard input
  useEffect(() => {
    const handleKeyDown = (e) => {
      keysRef.current[e.key.toLowerCase()] = true;
      keysRef.current[e.key] = true; // Also store original key
      
      if (gameState === 'playing') {
        // Number keys for tool selection
        if (e.key >= '1' && e.key <= '8') {
          const index = parseInt(e.key) - 1;
          if (phase === 1 && index < SCIENCE_OBJECTS.length) {
            setSelectedTool(index);
          } else if (phase === 2) {
            // Phase 2: 8 weapons total (4 science + 4 strategy)
            const totalWeapons = SCIENCE_OBJECTS.length + STRATEGY_TOOLS.length;
            if (index < totalWeapons) {
              setSelectedTool(index);
            }
          }
        }
        
        // Launch with A or Space
        if (e.key.toLowerCase() === 'a' || e.key === ' ') {
          e.preventDefault();
          if (launchRef.current) launchRef.current();
        }
      }
    };
    
    const handleKeyUp = (e) => {
      keysRef.current[e.key.toLowerCase()] = false;
      keysRef.current[e.key] = false;
    };
    
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [gameState, phase]);

  // Game loop
  useEffect(() => {
    if (gameState !== 'playing') return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    
    // Initialize refs from state
    founderRef.current = { ...founder };
    targetRef.current = { ...target };
    projectilesRef.current = [...projectiles];
    particlesRef.current = [...particles];
    
    let animationId;
    
    const gameLoop = () => {
      // Clear canvas
      ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
      
      // Draw background
      drawBackground(ctx);
      
      // Update founder position based on keys
      const keys = keysRef.current;
      if (keys['arrowleft'] || keys['ArrowLeft']) {
        founderRef.current.x = Math.max(FOUNDER_SIZE / 2, founderRef.current.x - 7);
      }
      if (keys['arrowright'] || keys['ArrowRight']) {
        founderRef.current.x = Math.min(CANVAS_WIDTH - FOUNDER_SIZE / 2, founderRef.current.x + 7);
      }
      
      // Update target position
      const t = targetRef.current;
      
      if (targetComingDown) {
        // Animate target coming down to meet founder
        const targetY = founderRef.current.y;
        const targetX = founderRef.current.x + 80;
        t.y += (targetY - t.y) * 0.05;
        t.x += (targetX - t.x) * 0.05;
        t.dodging = false;
      } else {
        // Normal movement - always keep within bounds
        t.x += t.direction * 3;
        
        // Clamp position to valid range first
        const minX = TARGET_SIZE + 20;
        const maxX = CANVAS_WIDTH - TARGET_SIZE - 20;
        t.x = Math.max(minX, Math.min(maxX, t.x));
        
        // Reverse direction at boundaries
        if (t.x <= minX || t.x >= maxX) {
          t.direction = -t.direction;
        }
        
        // Reset y position if it somehow drifted
        if (t.y < 60 || t.y > 120) {
          t.y = 80;
        }
        
        if (t.dodgeTimer > 0) {
          t.dodgeTimer--;
          if (t.dodgeTimer === 0) t.dodging = false;
        }
      }
      
      // Update projectiles
      const updatedProjectiles = [];
      for (const proj of projectilesRef.current) {
        proj.y -= proj.speed;
        
        // Homing for strategy projectiles
        if (proj.homing) {
          const dx = targetRef.current.x - proj.x;
          proj.x += dx * 0.05;
        }
        
        // Check collision with accelerator
        if (proj.type === 'accelerator_shot' && showAccelerator && !acceleratorHit) {
          const dist = Math.hypot(proj.x - accelerator.x, proj.y - accelerator.y);
          if (dist < ACCELERATOR_SIZE / 2 + PROJECTILE_SIZE / 2) {
            setAcceleratorHit(true);
            setShowAccelerator(false);
            setPhase(2);
            setCurrentReaction("Science is the engine. Strategy is the map.");
            createParticlesImmediate(accelerator.x, accelerator.y, 30, '#FFD700');
            setTimeout(() => setCurrentReaction(null), 3000);
            continue;
          }
        }
        
        // Check collision with target
        const targetDist = Math.hypot(proj.x - targetRef.current.x, proj.y - targetRef.current.y);
        const hitRadius = TARGET_SIZE / 2 + proj.width / 2;
        
        if (targetDist < hitRadius && proj.y < targetRef.current.y + TARGET_SIZE) {
          const weaponId = proj.data.id; // Get the specific weapon ID
          
          if (proj.type === 'science') {
            if (phase === 1) {
              // Phase 1: Target always dodges science - but count this as a hit attempt
              setScienceAttempts(prev => prev + 1);
              
              targetRef.current.dodging = true;
              targetRef.current.dodgeTimer = 30;
              
              // Dodge movement - keep within bounds
              const dodgeAmount = (Math.random() > 0.5 ? 80 : -80);
              const minX = TARGET_SIZE + 20;
              const maxX = CANVAS_WIDTH - TARGET_SIZE - 20;
              targetRef.current.x = Math.max(minX, Math.min(maxX, targetRef.current.x + dodgeAmount));
              
              // Weapon-specific dodge reaction
              const reaction = getWeaponReaction('science_dodge', weaponId);
              setCurrentReaction(reaction);
              setTimeout(() => setCurrentReaction(null), 2500);
              createParticlesImmediate(proj.x, proj.y, 10, '#888');
            } else {
              // Phase 2: Science weapons count as 0.5 hit - weapon-specific reaction
              const reaction = getWeaponReaction('science_hit', weaponId);
              setCurrentReaction(reaction);
              setTimeout(() => setCurrentReaction(null), 2500);
              
              setStrategyHits(prev => {
                const newHits = prev + 0.5;
                if (newHits >= 6) {
                  setTargetComingDown(true);
                  setDealDone(true);
                }
                return newHits;
              });
              createParticlesImmediate(proj.x, proj.y, 12, proj.data.color);
            }
            continue;
          } else if (proj.type === 'strategy') {
            // Strategy hits! Full point - weapon-specific reaction
            setStrategyHits(prev => {
              const newHits = prev + 1;
              if (newHits >= 6) {
                setTargetComingDown(true);
                setDealDone(true);
              }
              return newHits;
            });
            
            const reaction = getWeaponReaction('strategy_hit', weaponId);
            setCurrentReaction(reaction);
            setTimeout(() => setCurrentReaction(null), 2500);
            createParticlesImmediate(proj.x, proj.y, 20, proj.data.color);
            continue;
          }
        }
        
        // Keep projectile if still on screen
        if (proj.y > -PROJECTILE_SIZE) {
          updatedProjectiles.push(proj);
        }
      }
      projectilesRef.current = updatedProjectiles;
      
      // Update particles
      particlesRef.current = particlesRef.current.filter(p => p.life > 0).map(p => ({
        ...p,
        x: p.x + p.vx,
        y: p.y + p.vy,
        life: p.life - 1,
        vy: p.vy + 0.2
      }));
      
      // Sync state for React (less frequently to avoid performance issues)
      setFounder({ ...founderRef.current });
      setTarget({ ...targetRef.current });
      setProjectiles([...projectilesRef.current]);
      setParticles([...particlesRef.current]);
      
      // Draw everything
      drawGame(ctx);
      
      animationId = requestAnimationFrame(gameLoop);
    };
    
    // Helper to create particles immediately (in game loop)
    const createParticlesImmediate = (x, y, count, color) => {
      for (let i = 0; i < count; i++) {
        particlesRef.current.push({
          x,
          y,
          vx: (Math.random() - 0.5) * 8,
          vy: (Math.random() - 0.5) * 8 - 3,
          life: 30 + Math.random() * 20,
          color,
          size: 3 + Math.random() * 5
        });
      }
    };
    
    animationId = requestAnimationFrame(gameLoop);
    
    // Hide controls after 5 seconds
    const controlsTimer = setTimeout(() => setShowControls(false), 5000);
    
    return () => {
      cancelAnimationFrame(animationId);
      clearTimeout(controlsTimer);
    };
  }, [gameState, showAccelerator, acceleratorHit, targetType, targetComingDown]);

  // Check for accelerator appearance (separate effect) - appears after 5 unsuccessful science hits
  useEffect(() => {
    if (phase === 1 && scienceAttempts >= 5 && !showAccelerator && !acceleratorHit) {
      setShowAccelerator(true);
      setCurrentReaction("I believe the science is interesting. But science alone is not a strategy.");
      setTimeout(() => {
        setCurrentReaction("A new power-up has appeared! Click it or shoot it!");
      }, 3000);
    }
  }, [scienceAttempts, phase, showAccelerator, acceleratorHit]);

  // Check win condition - after deal done animation, go to won screen
  useEffect(() => {
    if (dealDone && gameState === 'playing') {
      // Wait for animation then show won screen
      setTimeout(() => {
        setGameState('won');
      }, 3000);
    }
  }, [dealDone, gameState]);

  // Click on accelerator to activate it
  const handleCanvasClick = (e) => {
    if (!showAccelerator || acceleratorHit) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    const dist = Math.hypot(x - accelerator.x, y - accelerator.y);
    if (dist < ACCELERATOR_SIZE) {
      setAcceleratorHit(true);
      setShowAccelerator(false);
      setPhase(2);
      setCurrentReaction("Science is the engine. Strategy is the map.");
      createParticles(accelerator.x, accelerator.y, 30, '#FFD700');
      setTimeout(() => setCurrentReaction(null), 3000);
    }
  };

  // Create particles
  const createParticles = (x, y, count, color) => {
    const newParticles = [];
    for (let i = 0; i < count; i++) {
      newParticles.push({
        x,
        y,
        vx: (Math.random() - 0.5) * 8,
        vy: (Math.random() - 0.5) * 8 - 3,
        life: 30 + Math.random() * 20,
        color,
        size: 3 + Math.random() * 5
      });
    }
    setParticles(prev => [...prev, ...newParticles]);
  };

  // Draw background
  const drawBackground = (ctx) => {
    // Gradient background
    const gradient = ctx.createLinearGradient(0, 0, 0, CANVAS_HEIGHT);
    if (phase === 1) {
      gradient.addColorStop(0, '#1a1a2e');
      gradient.addColorStop(1, '#16213e');
    } else {
      gradient.addColorStop(0, '#0f172a');
      gradient.addColorStop(1, '#1e3a5f');
    }
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    
    // Stars
    ctx.fillStyle = '#ffffff';
    for (let i = 0; i < 50; i++) {
      const x = (i * 137) % CANVAS_WIDTH;
      const y = (i * 97) % CANVAS_HEIGHT;
      const size = (i % 3) + 1;
      ctx.beginPath();
      ctx.arc(x, y, size, 0, Math.PI * 2);
      ctx.fill();
    }
    
    // Lab equipment silhouettes
    ctx.fillStyle = 'rgba(255,255,255,0.05)';
    ctx.fillRect(50, CANVAS_HEIGHT - 150, 80, 100);
    ctx.fillRect(CANVAS_WIDTH - 130, CANVAS_HEIGHT - 180, 80, 130);
  };

  // Draw game entities
  const drawGame = (ctx) => {
    // Draw accelerator
    if (showAccelerator && !acceleratorHit) {
      ctx.save();
      const pulse = Math.sin(Date.now() / 200) * 10;
      
      // Glow
      const glow = ctx.createRadialGradient(accelerator.x, accelerator.y, 0, accelerator.x, accelerator.y, ACCELERATOR_SIZE + pulse);
      glow.addColorStop(0, 'rgba(255, 215, 0, 0.8)');
      glow.addColorStop(0.5, 'rgba(255, 165, 0, 0.4)');
      glow.addColorStop(1, 'rgba(255, 215, 0, 0)');
      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.arc(accelerator.x, accelerator.y, ACCELERATOR_SIZE + pulse, 0, Math.PI * 2);
      ctx.fill();
      
      // Icon
      ctx.fillStyle = '#FFD700';
      ctx.font = 'bold 40px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('🚀', accelerator.x, accelerator.y);
      
      // Label
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 14px Arial';
      ctx.fillText('ACCELERATOR', accelerator.x, accelerator.y + 50);
      ctx.font = '12px Arial';
      ctx.fillText('(Click or shoot to activate!)', accelerator.x, accelerator.y + 68);
      
      ctx.restore();
    }
    
    // Draw particles - use ref for real-time
    for (const p of particlesRef.current) {
      ctx.fillStyle = p.color;
      ctx.globalAlpha = p.life / 50;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
    
    // Draw projectiles - use ref for real-time
    for (const proj of projectilesRef.current) {
      ctx.save();
      ctx.translate(proj.x, proj.y);
      
      if (proj.type === 'science' || proj.type === 'accelerator_shot') {
        // Science object
        ctx.fillStyle = proj.data.color;
        ctx.beginPath();
        ctx.roundRect(-proj.width / 2, -proj.height / 2, proj.width, proj.height, 8);
        ctx.fill();
        
        ctx.font = '24px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(proj.data.icon, 0, 0);
      } else if (proj.type === 'strategy') {
        // Strategy combination
        ctx.fillStyle = proj.data.color;
        ctx.shadowColor = proj.data.color;
        ctx.shadowBlur = 15;
        ctx.beginPath();
        ctx.roundRect(-proj.width / 2, -proj.height / 2, proj.width, proj.height, 12);
        ctx.fill();
        ctx.shadowBlur = 0;
        
        ctx.fillStyle = '#fff';
        ctx.font = 'bold 10px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(proj.data.name, 0, 0);
      }
      
      ctx.restore();
    }
    
    // Draw target - use ref for real-time position
    ctx.save();
    ctx.translate(targetRef.current.x, targetRef.current.y);
    
    if (targetRef.current.dodging) {
      ctx.rotate(Math.sin(Date.now() / 50) * 0.2);
    }
    
    // Target body
    ctx.fillStyle = selectedCharacter?.color || '#4F46E5';
    ctx.beginPath();
    ctx.arc(0, 0, TARGET_SIZE / 2, 0, Math.PI * 2);
    ctx.fill();
    
    // Face
    ctx.fillStyle = '#fff';
    ctx.beginPath();
    ctx.arc(-12, -8, 8, 0, Math.PI * 2);
    ctx.arc(12, -8, 8, 0, Math.PI * 2);
    ctx.fill();
    
    ctx.fillStyle = '#333';
    ctx.beginPath();
    ctx.arc(-12, -8, 4, 0, Math.PI * 2);
    ctx.arc(12, -8, 4, 0, Math.PI * 2);
    ctx.fill();
    
    // Mouth
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 2;
    ctx.beginPath();
    if (dealDone) {
      // Big happy smile when deal is done
      ctx.arc(0, 8, 12, 0.1, Math.PI - 0.1);
    } else if (targetRef.current.dodging) {
      ctx.arc(0, 10, 10, 0, Math.PI);
    } else {
      ctx.moveTo(-10, 12);
      ctx.lineTo(10, 12);
    }
    ctx.stroke();
    
    ctx.restore();
    
    // Draw founder - use ref for real-time position
    ctx.save();
    ctx.translate(founderRef.current.x, founderRef.current.y);
    
    // Founder body
    ctx.fillStyle = '#10B981';
    ctx.beginPath();
    ctx.arc(0, 0, FOUNDER_SIZE / 2, 0, Math.PI * 2);
    ctx.fill();
    
    // Lab coat
    ctx.fillStyle = '#fff';
    ctx.fillRect(-20, 5, 40, 25);
    
    // Face
    ctx.fillStyle = '#fff';
    ctx.beginPath();
    ctx.arc(-10, -8, 6, 0, Math.PI * 2);
    ctx.arc(10, -8, 6, 0, Math.PI * 2);
    ctx.fill();
    
    ctx.fillStyle = '#333';
    ctx.beginPath();
    ctx.arc(-10, -8, 3, 0, Math.PI * 2);
    ctx.arc(10, -8, 3, 0, Math.PI * 2);
    ctx.fill();
    
    // Smile - bigger when deal done
    ctx.strokeStyle = '#333';
    ctx.lineWidth = 2;
    ctx.beginPath();
    if (dealDone) {
      ctx.arc(0, 5, 10, 0.1, Math.PI - 0.1);
    } else {
      ctx.arc(0, 5, 8, 0.2, Math.PI - 0.2);
    }
    ctx.stroke();
    
    ctx.restore();
    
    // Draw "DEAL DONE" message when deal is complete
    if (dealDone) {
      ctx.save();
      
      // Background overlay
      ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
      ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
      
      // Confetti particles
      for (let i = 0; i < 50; i++) {
        const x = (Date.now() / 10 + i * 73) % CANVAS_WIDTH;
        const y = (Date.now() / 15 + i * 47) % CANVAS_HEIGHT;
        ctx.fillStyle = ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4'][i % 5];
        ctx.beginPath();
        ctx.arc(x, y, 4 + (i % 3), 0, Math.PI * 2);
        ctx.fill();
      }
      
      // "DEAL DONE" text
      const centerX = CANVAS_WIDTH / 2;
      const centerY = CANVAS_HEIGHT / 2 - 50;
      
      // Glow effect
      ctx.shadowColor = '#FFD700';
      ctx.shadowBlur = 30;
      
      ctx.fillStyle = '#FFD700';
      ctx.font = 'bold 72px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('🤝 DEAL DONE! 🤝', centerX, centerY);
      
      ctx.shadowBlur = 0;
      
      ctx.restore();
    }
    
    // Draw reaction bubble - STATIC at top center for readability
    if (currentReaction && !dealDone) {
      ctx.save();
      const bubbleX = CANVAS_WIDTH / 2;
      const bubbleY = 160;
      const maxWidth = 350;
      
      // Background with slight shadow
      ctx.shadowColor = 'rgba(0,0,0,0.3)';
      ctx.shadowBlur = 10;
      ctx.fillStyle = 'rgba(255,255,255,0.98)';
      ctx.beginPath();
      ctx.roundRect(bubbleX - maxWidth / 2, bubbleY, maxWidth, 70, 12);
      ctx.fill();
      ctx.shadowBlur = 0;
      
      ctx.fillStyle = '#333';
      ctx.font = '14px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      
      // Word wrap
      const words = currentReaction.split(' ');
      let line = '';
      let y = bubbleY + 25;
      for (const word of words) {
        const testLine = line + word + ' ';
        if (ctx.measureText(testLine).width > maxWidth - 30) {
          ctx.fillText(line, bubbleX, y);
          line = word + ' ';
          y += 18;
        } else {
          line = testLine;
        }
      }
      ctx.fillText(line, bubbleX, y);
      
      ctx.restore();
    }
    
    // Draw founder's comment bubble - near the founder
    if (founderCommentRef.current && !dealDone) {
      ctx.save();
      const bubbleX = founderRef.current.x;
      const bubbleY = founderRef.current.y - 80;
      const maxWidth = 200;
      
      // Background with green tint (founder's color)
      ctx.shadowColor = 'rgba(16, 185, 129, 0.5)';
      ctx.shadowBlur = 10;
      ctx.fillStyle = 'rgba(16, 185, 129, 0.95)';
      ctx.beginPath();
      ctx.roundRect(bubbleX - maxWidth / 2, bubbleY - 20, maxWidth, 40, 10);
      ctx.fill();
      ctx.shadowBlur = 0;
      
      // Small triangle pointing down to founder
      ctx.beginPath();
      ctx.moveTo(bubbleX - 8, bubbleY + 20);
      ctx.lineTo(bubbleX + 8, bubbleY + 20);
      ctx.lineTo(bubbleX, bubbleY + 32);
      ctx.closePath();
      ctx.fill();
      
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 13px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(founderCommentRef.current, bubbleX, bubbleY);
      
      ctx.restore();
    }
    
    // Draw controls hint
    if (showControls) {
      ctx.fillStyle = 'rgba(0,0,0,0.7)';
      ctx.fillRect(CANVAS_WIDTH / 2 - 150, CANVAS_HEIGHT / 2 - 40, 300, 80);
      ctx.fillStyle = '#fff';
      ctx.font = '14px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('Move with ← →', CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2 - 15);
      ctx.fillText('Select your evidence', CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2 + 5);
      ctx.fillText('Press A or SPACE to pitch', CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2 + 25);
    }
  };

  // Start game
  const startGame = () => {
    if (!selectedCharacter) return;
    setGameState('playing');
    setPhase(1);
    setScienceAttempts(0);
    setShowAccelerator(false);
    setAcceleratorHit(false);
    setProjectiles([]);
    setParticles([]);
    setCurrentReaction(null);
    setFounderComment(null);
    setShowControls(true);
    setStrategyHits(0);
    setDealDone(false);
    setTargetComingDown(false);
    setSelectedTool(0);
    setFounder({ x: CANVAS_WIDTH / 2, y: CANVAS_HEIGHT - 100 });
    setTarget({ x: CANVAS_WIDTH / 2, y: 80, direction: 1, dodging: false, dodgeTimer: 0 });
  };

  // Restart game
  const restartGame = () => {
    setGameState('start');
    setTargetType(null);
    setSelectedCharacter(null);
  };

  // Render start screen
  const renderStartScreen = () => (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-b from-slate-900 via-purple-900 to-slate-900 p-8">
      <button onClick={() => navigate(-1)} className="absolute top-4 left-4 text-white hover:text-purple-300 flex items-center gap-2">
        <ArrowLeft className="w-5 h-5" /> Back
      </button>
      
      <h1 className="text-5xl font-bold text-white mb-2 text-center" style={{ textShadow: '0 0 20px #8B5CF6' }}>
        🧬 SCIENCE INVADERS 🚀
      </h1>
      <p className="text-xl text-purple-300 mb-8 text-center">
        Great science gets attention. Strategy creates partnerships.
      </p>
      
      {!targetType ? (
        <div className="space-y-4">
          <p className="text-white text-center mb-4">Who are you trying to attract?</p>
          <div className="flex gap-4">
            <Button onClick={() => setTargetType('investor')} className="bg-indigo-600 hover:bg-indigo-500 text-white px-8 py-6 text-lg">
              💰 INVESTOR
            </Button>
            <Button onClick={() => setTargetType('pharma')} className="bg-teal-600 hover:bg-teal-500 text-white px-8 py-6 text-lg">
              💊 BIG PHARMA
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <p className="text-white text-center">Choose your target:</p>
          <div className="grid grid-cols-2 gap-4 max-w-2xl">
            {(targetType === 'investor' ? INVESTOR_CHARACTERS : PHARMA_CHARACTERS).map(char => (
              <div
                key={char.id}
                onClick={() => setSelectedCharacter(char)}
                className={`p-4 rounded-lg cursor-pointer transition-all ${
                  selectedCharacter?.id === char.id
                    ? 'bg-white/20 ring-2 ring-white'
                    : 'bg-white/5 hover:bg-white/10'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center text-2xl" style={{ backgroundColor: char.color }}>
                    {targetType === 'investor' ? '💼' : '🔬'}
                  </div>
                  <div>
                    <h3 className="text-white font-bold">{char.name}</h3>
                    <p className="text-gray-400 text-sm">"{char.description}"</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="flex gap-4 justify-center">
            <Button onClick={() => setTargetType(null)} variant="outline" className="border-white/30 text-white hover:bg-white/10">
              ← Back
            </Button>
            <Button onClick={startGame} disabled={!selectedCharacter} className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white px-8">
              🎮 START PITCHING
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  // Render game UI
  const renderGameUI = () => (
    <div className="flex flex-col items-center bg-slate-900 min-h-screen">
      {/* Top bar */}
      <div className="w-full max-w-[1200px] flex justify-between items-center p-2 bg-slate-800/50">
        <div className="flex items-center gap-4">
          <span className="text-white font-bold">Target: {selectedCharacter?.name}</span>
          {phase === 1 && (
            <span className="text-yellow-400">Science hits (dodged): {scienceAttempts} / 5</span>
          )}
        </div>
        
        {/* Critical Hits - always shown */}
        {!dealDone && (
          <div className="flex items-center gap-2">
            <span className="text-white">Critical Hits:</span>
            <div className="flex gap-1">
              {[...Array(6)].map((_, i) => {
                const filled = i < Math.floor(strategyHits);
                const halfFilled = !filled && i < strategyHits;
                return (
                  <div 
                    key={i} 
                    className={`w-6 h-6 rounded-full border-2 ${
                      filled ? 'bg-gradient-to-r from-green-500 to-emerald-400 border-green-400' : 
                      halfFilled ? 'bg-gradient-to-r from-green-500/50 to-emerald-400/50 border-green-400/50' :
                      'bg-slate-700 border-slate-600'
                    }`}
                  />
                );
              })}
            </div>
            <span className="text-white font-bold">{strategyHits.toFixed(1)} / 6</span>
          </div>
        )}
        
        <div className="flex gap-2">
          <Button size="sm" variant="ghost" onClick={() => setSoundEnabled(!soundEnabled)} className="text-white">
            {soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setGameState(gameState === 'paused' ? 'playing' : 'paused')} className="text-white">
            {gameState === 'paused' ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
          </Button>
          <Button size="sm" variant="ghost" onClick={restartGame} className="text-white">
            <RotateCcw className="w-4 h-4" />
          </Button>
        </div>
      </div>
      
      {/* Canvas */}
      <canvas
        ref={canvasRef}
        width={CANVAS_WIDTH}
        height={CANVAS_HEIGHT}
        className="border border-slate-700 cursor-pointer"
        onClick={handleCanvasClick}
      />
      
      {/* Bottom toolbar */}
      <div className="w-full max-w-[1200px] bg-slate-800/80 p-3">
        {phase === 1 ? (
          <div className="flex justify-center gap-2">
            {SCIENCE_OBJECTS.map((obj, i) => (
              <button
                key={obj.id}
                onClick={() => setSelectedTool(i)}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-all ${
                  selectedTool === i ? 'ring-2 ring-white' : ''
                }`}
                style={{ backgroundColor: obj.color }}
              >
                <span className="text-xl">{obj.icon}</span>
                <span className="text-white text-sm font-medium">{obj.name}</span>
                <span className="text-white/60 text-xs">({i + 1})</span>
              </button>
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {/* Phase 2: All weapons - Science + Strategy */}
            <div className="text-center text-xs text-slate-400 mb-2">
              Strategy hits: {strategyHits} / 6 — Science still dodged, Strategy weapons hit!
            </div>
            <div className="flex flex-wrap justify-center gap-2">
              {/* Science weapons */}
              {SCIENCE_OBJECTS.map((obj, i) => (
                <button
                  key={obj.id}
                  onClick={() => setSelectedTool(i)}
                  className={`px-3 py-2 rounded-lg flex items-center gap-2 transition-all ${
                    selectedTool === i ? 'ring-2 ring-white' : 'opacity-70'
                  }`}
                  style={{ backgroundColor: obj.color }}
                >
                  <span className="text-lg">{obj.icon}</span>
                  <span className="text-white text-xs font-medium">{obj.name}</span>
                  <span className="text-white/60 text-xs">({i + 1})</span>
                </button>
              ))}
              
              {/* Divider */}
              <div className="w-px bg-slate-600 mx-2"></div>
              
              {/* Strategy weapons */}
              {STRATEGY_TOOLS.map((tool, i) => (
                <button
                  key={tool.id}
                  onClick={() => setSelectedTool(SCIENCE_OBJECTS.length + i)}
                  className={`px-3 py-2 rounded-lg flex items-center gap-2 transition-all ${
                    selectedTool === SCIENCE_OBJECTS.length + i ? 'ring-2 ring-white ring-offset-2 ring-offset-slate-800' : ''
                  }`}
                  style={{ backgroundColor: tool.color }}
                >
                  <span className="text-lg">{tool.icon}</span>
                  <span className="text-white text-xs font-medium">{tool.name}</span>
                  <span className="text-white/60 text-xs">({SCIENCE_OBJECTS.length + i + 1})</span>
                </button>
              ))}
            </div>
          </div>
        )}
        
        {/* Mobile controls */}
        <div className="flex justify-center gap-4 mt-3 md:hidden">
          <Button 
            onTouchStart={() => keysRef.current['arrowleft'] = true}
            onTouchEnd={() => keysRef.current['arrowleft'] = false}
            className="w-16 h-16 text-2xl"
          >
            ←
          </Button>
          <Button onClick={launchProjectile} className="w-16 h-16 bg-purple-600 text-lg">
            PITCH
          </Button>
          <Button 
            onTouchStart={() => keysRef.current['arrowright'] = true}
            onTouchEnd={() => keysRef.current['arrowright'] = false}
            className="w-16 h-16 text-2xl"
          >
            →
          </Button>
        </div>
      </div>
    </div>
  );

  // Render win screen - simple, no teaching lessons
  const renderWinScreen = () => (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-b from-emerald-900 via-teal-900 to-slate-900 p-8 text-center">
      <div className="text-8xl mb-6">🤝</div>
      <h1 className="text-5xl font-bold text-white mb-8">
        {targetType === 'investor' ? 'DEAL DONE! 💰' : 'DEAL DONE! 💊'}
      </h1>
      
      <div className="flex gap-4 flex-wrap justify-center">
        <Button onClick={restartGame} className="bg-purple-600 hover:bg-purple-500 text-lg px-8 py-3">
          🎮 Play Again
        </Button>
        <Button onClick={() => navigate('/')} variant="outline" className="border-white/30 text-white hover:bg-white/10 text-lg px-8 py-3">
          Back to Home
        </Button>
      </div>
    </div>
  );

  // Render paused overlay
  const renderPausedOverlay = () => (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
      <div className="bg-slate-800 p-8 rounded-xl text-center">
        <h2 className="text-3xl font-bold text-white mb-4">⏸️ PAUSED</h2>
        <Button onClick={() => setGameState('playing')} className="bg-purple-600 hover:bg-purple-500">
          Resume
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen">
      {gameState === 'start' && renderStartScreen()}
      {(gameState === 'playing' || gameState === 'paused') && renderGameUI()}
      {gameState === 'paused' && renderPausedOverlay()}
      {gameState === 'won' && renderWinScreen()}
    </div>
  );
}
